import React from 'react';
import { useSystemActivities } from '../hooks/useSystemActivities';
import { RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';
import { IconButton } from './UI';

const SystemActivities: React.FC = () => {
  const {
    activities,
    loading,
    error,
    fetchActivities,
    getActivityIcon,
    getActivityColor,
    formatTimestamp
  } = useSystemActivities();

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Atividades Recentes</h3>
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
        </div>
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg animate-pulse">
              <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Atividades Recentes</h3>
          <IconButton action="refresh" label="Tentar novamente" onClick={fetchActivities} />
        </div>
        <div className="flex items-center justify-center py-4 text-amber-600 bg-amber-50 rounded-lg">
          <AlertCircle className="w-5 h-5 mr-2" />
          <span className="text-sm">{error}</span>
        </div>
        {activities.length > 0 && (
          <div className="mt-4">
            <p className="text-xs text-gray-500 mb-3">Dados simulados:</p>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {activities.map((activity) => (
                <div
                  key={activity.id}
                  className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg"
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${getActivityColor(activity.type)}`}>
                    {getActivityIcon(activity.type)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {activity.description}
                      </p>
                      <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                        {formatTimestamp(activity.timestamp)}
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-2 mt-1">
                      <span className="text-xs text-gray-500">
                        {activity.user?.name || 'Sistema'}
                      </span>
                      {activity.metadata?.ip && (
                        <>
                          <span className="text-xs text-gray-400">•</span>
                          <span className="text-xs text-gray-500">
                            {activity.metadata.ip}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Atividades Recentes</h3>
        <IconButton action="refresh" label="Atualizar atividades" onClick={fetchActivities} />
      </div>

      <div className="space-y-3 max-h-96 overflow-y-auto">
        {activities.length === 0 ? (
          <div className="flex items-center justify-center py-8 text-gray-500">
            <CheckCircle className="w-5 h-5 mr-2" />
            <span>Nenhuma atividade recente</span>
          </div>
        ) : (
          activities.map((activity) => (
            <div
              key={activity.id}
              className="flex items-start space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors border border-gray-100"
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${getActivityColor(activity.type)}`}>
                {getActivityIcon(activity.type)}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {activity.description}
                  </p>
                  <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                    {formatTimestamp(activity.timestamp)}
                  </span>
                </div>
                
                <div className="flex items-center space-x-2 mt-1">
                  <span className="text-xs text-gray-500">
                    {activity.user?.name || 'Sistema'}
                  </span>
                  {activity.metadata?.ip && (
                    <>
                      <span className="text-xs text-gray-400">•</span>
                      <span className="text-xs text-gray-500">
                        {activity.metadata.ip}
                      </span>
                    </>
                  )}
                </div>

                {activity.metadata?.details && (
                  <div className="mt-2 text-xs text-gray-600 bg-gray-50 p-2 rounded">
                    {activity.type === 'post' && activity.metadata.details.title && (
                      <div className="flex items-center space-x-1">
                        <span className="font-medium">Título:</span>
                        <span>{activity.metadata.details.title}</span>
                      </div>
                    )}
                    {activity.type === 'contact' && activity.metadata.details.subject && (
                      <div className="flex items-center space-x-1">
                        <span className="font-medium">Assunto:</span>
                        <span>{activity.metadata.details.subject}</span>
                      </div>
                    )}
                    {activity.type === 'settings' && activity.metadata.details.section && (
                      <div className="flex items-center space-x-1">
                        <span className="font-medium">Seção:</span>
                        <span className="px-2 py-1 bg-primary-100 text-primary-800 rounded text-xs">
                          {activity.metadata.details.section}
                        </span>
                      </div>
                    )}
                    {activity.type === 'backup' && activity.metadata.details.size && (
                      <div className="flex items-center space-x-1">
                        <span className="font-medium">Tamanho:</span>
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                          {activity.metadata.details.size}
                        </span>
                      </div>
                    )}
                    {activity.type === 'backup' && activity.metadata.details.type && (
                      <div className="flex items-center space-x-1">
                        <span className="font-medium">Tipo:</span>
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                          {activity.metadata.details.type}
                        </span>
                      </div>
                    )}
                    {activity.type === 'backup' && activity.metadata.details.status && (
                      <div className="flex items-center space-x-1">
                        <span className="font-medium">Status:</span>
                        <span className={`px-2 py-1 rounded text-xs ${
                          activity.metadata.details.status === 'completed' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {activity.metadata.details.status}
                        </span>
                      </div>
                    )}
                    {activity.type === 'backup' && activity.metadata.details.duration && (
                      <div className="flex items-center space-x-1">
                        <span className="font-medium">Duração:</span>
                        <span className="text-xs">{activity.metadata.details.duration}</span>
                      </div>
                    )}
                    {activity.type === 'backup' && activity.metadata.details.uptime && (
                      <div className="flex items-center space-x-1">
                        <span className="font-medium">Uptime:</span>
                        <span className="text-xs">{activity.metadata.details.uptime}</span>
                      </div>
                    )}
                    {activity.type === 'backup' && activity.metadata.details.connections && (
                      <div className="flex items-center space-x-1">
                        <span className="font-medium">Conexões:</span>
                        <span className="text-xs">{activity.metadata.details.connections}</span>
                      </div>
                    )}
                    {activity.type === 'backup' && activity.metadata.details.responseTime && (
                      <div className="flex items-center space-x-1">
                        <span className="font-medium">Tempo de resposta:</span>
                        <span className="text-xs">{activity.metadata.details.responseTime}</span>
                      </div>
                    )}
                    {activity.type === 'login' && activity.metadata.userAgent && (
                      <div className="flex items-center space-x-1">
                        <span className="font-medium">Navegador:</span>
                        <span className="text-xs">{activity.metadata.userAgent}</span>
                      </div>
                    )}
                    {activity.type === 'login' && activity.metadata.location && (
                      <div className="flex items-center space-x-1">
                        <span className="font-medium">Localização:</span>
                        <span className="text-xs">{activity.metadata.location}</span>
                      </div>
                    )}
                    {activity.type === 'login' && activity.metadata.device && (
                      <div className="flex items-center space-x-1">
                        <span className="font-medium">Dispositivo:</span>
                        <span className="text-xs">{activity.metadata.device}</span>
                      </div>
                    )}
                    {activity.type === 'error' && activity.metadata.details.error && (
                      <div className="flex items-center space-x-1">
                        <span className="font-medium text-red-600">Erro:</span>
                        <span className="text-red-600 text-xs">{activity.metadata.details.error}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {activities.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between text-sm text-gray-500">
            <div className="flex items-center space-x-4">
              <span>Última atualização: {formatTimestamp(new Date().toISOString())}</span>
              <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                {activities.length} atividade{activities.length !== 1 ? 's' : ''}
              </span>
            </div>
            <button
              onClick={fetchActivities}
              className="text-primary-600 hover:text-primary-700 transition-colors text-xs"
              title="Atualizar atividades"
            >
              <RefreshCw className="w-3 h-3 inline mr-1" />
              Atualizar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemActivities;
